import React, { FormEvent, useCallback, useEffect, useState } from "react";
import { api, Group } from "../api/client";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";

const DEFAULT_COLOR = "#3B82F6";

type Editing = { id: string; name: string; color: string };

export default function GroupsPage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [editing, setEditing] = useState<Editing | null>(null);
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const list = await api.listGroups();
      setGroups((list ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, "ru")));
    } catch (err: any) {
      console.error("Failed to load groups", err);
      setError(err?.message || "Не удалось загрузить группы");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action: () => Promise<unknown>, failure: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (err: any) {
      console.error("Group action failed", err);
      setError(err?.message || failure);
      return false;
    } finally {
      setBusy(false);
      await load();
    }
  };

  const newName = name.trim();

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!newName) return;
    const created = await run(() => api.createGroup({ name: newName, color }), "Не удалось создать группу");
    if (created) {
      setName("");
      setColor(DEFAULT_COLOR);
    }
  };

  const save = async (group: Group) => {
    if (!editing || !editing.name.trim()) return;
    const saved = await run(
      () => api.updateGroup(group, { name: editing.name.trim(), color: editing.color }),
      "Не удалось сохранить группу"
    );
    if (saved) setEditing(null);
  };

  const createChat = async (group: Group) => {
    const confirmed = await confirm({
      title: "Создать командный чат?",
      body: (
        <>
          <p>
            В чат «{group.name}» попадут дети группы, её тренеры и вы. Родители общаются
            отдельно, в чате «{group.name} — родители».
          </p>
        </>
      ),
      confirmLabel: "Создать чат",
    });
    if (!confirmed) return;
    await run(() => api.createGroupChat(group.id, group.name), "Не удалось создать чат группы");
  };

  const remove = async (group: Group) => {
    const confirmed = await confirm({
      title: `Удалить группу «${group.name}»?`,
      body: (
        <>
          <p>
            Вместе с группой удалятся её недельное расписание и запланированные тренировки,
            дежурства, награды и медали детей за эту группу, стоимость группы и назначения
            тренеров.
          </p>
          <p>
            Дети, прошедшие тренировки и тесты нормативов останутся без группы. Чаты группы
            останутся.
          </p>
          <p>Отменить удаление нельзя.</p>
        </>
      ),
      confirmLabel: "Удалить группу",
      danger: true,
    });
    if (!confirmed) return;
    await run(() => api.deleteGroup(group.id), "Не удалось удалить группу");
  };

  if (loading) return <PageLoader />;

  return (
    <div className="page page-wide">
      <h2>Группы</h2>
      <p className="page-description">
        Команды клуба. У каждой группы есть командный чат для детей и тренеров и отдельный
        чат для родителей. Родительский чат сервер создаёт сам вместе с группой.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      <form className="mav-card cp-form group-form" aria-label="Новая группа" onSubmit={create}>
        <label className="cp-field">
          <span>Название</span>
          <input
            className="mav-input"
            value={name}
            maxLength={100}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="cp-field group-color-field">
          <span>Цвет</span>
          <input
            type="color"
            className="group-color-input"
            value={color}
            disabled={busy}
            onChange={(e) => setColor(e.target.value)}
          />
        </label>
        <div className="cp-actions">
          <button type="submit" className="mav-btn-primary" disabled={busy || !newName}>
            Создать группу
          </button>
        </div>
      </form>

      <div className="table-wrap">
        <table className="settings-table stack-table group-table">
          <thead>
            <tr>
              <th>Группа</th>
              <th>Командный чат</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 ? (
              <tr>
                <td colSpan={3} className="empty-cell">
                  {error ? "—" : "Групп пока нет"}
                </td>
              </tr>
            ) : (
              groups.map((g) =>
                editing?.id === g.id ? (
                  <tr key={g.id}>
                    <td className="cell-group-name">
                      <input
                        className="mav-input"
                        aria-label="Название группы"
                        value={editing.name}
                        maxLength={100}
                        disabled={busy}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      />
                    </td>
                    <td className="cell-group-chat">
                      <input
                        type="color"
                        className="group-color-input"
                        aria-label="Цвет группы"
                        value={editing.color}
                        disabled={busy}
                        onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                      />
                    </td>
                    <td className="cell-action">
                      <div className="row-actions">
                        <button
                          className="mav-btn-primary"
                          disabled={busy || !editing.name.trim()}
                          onClick={() => save(g)}
                        >
                          Сохранить
                        </button>
                        <button className="mav-btn-secondary" disabled={busy} onClick={() => setEditing(null)}>
                          Отмена
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={g.id}>
                    <td className="cell-group-name">
                      <span className="group-name">
                        <span className="group-swatch" style={{ background: g.color || DEFAULT_COLOR }} aria-hidden="true" />
                        {g.name}
                      </span>
                    </td>
                    <td className="cell-group-chat">
                      {g.chat_id ? (
                        <span className="mav-badge mav-badge-active">Есть чат</span>
                      ) : (
                        <span className="mav-badge mav-badge-muted">Нет чата</span>
                      )}
                    </td>
                    <td className="cell-action">
                      <div className="row-actions">
                        <button
                          className="mav-btn-secondary"
                          disabled={busy}
                          onClick={() => setEditing({ id: g.id, name: g.name, color: g.color || DEFAULT_COLOR })}
                        >
                          Изменить
                        </button>
                        {!g.chat_id && (
                          <button className="mav-btn-secondary" disabled={busy} onClick={() => createChat(g)}>
                            Создать чат
                          </button>
                        )}
                        <button className="btn-quiet-danger" disabled={busy} onClick={() => remove(g)}>
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </table>
      </div>

      {dialog}
    </div>
  );
}
